import React from "react";
import { Heart } from "@phosphor-icons/react";

import { Empty, LoadingBlock } from "../../../design-system/components/desktop";
import { ListingCard, ListingGrid } from "../../../design-system/patterns/market";
import { mediaUrl } from "../../../utils/common";

/**
 * The buyer's saved cards, four of them. Pure.
 *
 * These are the SAME card the marketplace, the store and the sets pages render,
 * so they are the shared `ListingCard` and not a fifth implementation of it.
 * The old `.pf-card-item` row with its `.view-text` and its arrow is gone.
 *
 * No price is shown, because the favourites payload does not carry one. A card
 * with a price slot filled by nothing reads as free.
 *
 * The card is an `href` and therefore a real link, not a div with an `onClick`
 * the way it used to be. That is what makes it reachable by keyboard, openable
 * in a new tab, and announced as a link rather than as nothing at all.
 */
const SavedCardsSection = ({ cards = [], isLoading = false }) => {
  if (isLoading) return <LoadingBlock>Loading your saved cards</LoadingBlock>;

  if (!cards.length) {
    return (
      /* The SAME wishlist `SavedTable` renders. It said "No saved cards yet"
         with a Heart while the table said "Your collection is empty" with a
         Tray, so one feature read as two. Both say this now. This surface is a
         panel inside the profile, so it keeps the GLYPH treatment and stays
         `inline`: the illustration belongs on the table that owns a whole page.
         See illustrations/README.md. */
      <Empty
        icon={Heart}
        illustration="no-saved-items-yet"
        title="Your collection is empty"
        text="Tap the heart on any card and it will be waiting for you here."
        inline
      />
    );
  }

  return (
    <ListingGrid columns={4}>
      {cards.slice(0, 4).map((card) => (
        <ListingCard
          key={card.card_id}
          mode="tile"
          image={mediaUrl(card.image_url)}
          imageAlt={card.name || "Card"}
          set={card.set_name}
          name={card.name || "Unnamed card"}
          rarity={card.rarity}
          href={`/marketplace/card/${card.product}`}
        />
      ))}
    </ListingGrid>
  );
};

export default SavedCardsSection;
